/**
 * Tail Assignment Optimizer
 *
 * Algorithms:
 *   greedyTailSchedule       - greedy chain-builder, O(n log n)
 *   localSearchTailSchedule  - greedy + targeted swap repair
 *   optimizeTailAssignment   - main entry point
 *
 * Constraints (applied per consecutive flight pair on the same tail):
 *   CONNECTIVITY   - arrival airport must equal next departure airport
 *   MIN_TURNAROUND - minimum ground time between flights (default 25 min)
 *
 * Returns: { assignments: Map<flightId, flightReg>, changes, stats }
 */
import { MINIMUM_TAT_MINUTES } from 'state/constants';

// ─── CONSTRAINT DEFINITIONS ───────────────────────────────────────────────────
// Same pattern as laborRules.js — add new rules here, engine picks them up.

export const TAIL_CONSTRAINT_RULES = [
    {
        id: 'CONNECTIVITY',
        name: 'Airport connectivity',
        description: 'Arrival airport of flight[i] must equal departure airport of flight[i+1].',
        severity: 'ERROR',
        weight: 100,
        check: (prev, next) => prev.arr === next.dep,
    },
    {
        id: 'MIN_TURNAROUND',
        name: 'Minimum ground turnaround',
        description: `Aircraft needs at least ${MINIMUM_TAT_MINUTES} min on ground between flights.`,
        severity: 'WARNING',
        weight: 1,
        defaultParam: MINIMUM_TAT_MINUTES,
        check: (prev, next, param = MINIMUM_TAT_MINUTES) => {
            const eta = new Date(prev.fullEta || prev.fullSta).getTime();
            const std = new Date(next.fullStd || next.fullEtd).getTime();
            if (isNaN(eta) || isNaN(std)) return true;
            return (std - eta) / 60000 >= param;
        },
    },
];

// ─── INTERNAL HELPERS ─────────────────────────────────────────────────────────

const getEtd = (f) => new Date(f.fullEtd || f.fullStd).getTime();
const getEta = (f) => new Date(f.fullEta || f.fullSta).getTime();

function buildTailBook(flights, assignments) {
    const book = new Map();
    for (const f of flights) {
        const reg = assignments.get(f._id) ?? f.flightReg;
        if (!book.has(reg)) book.set(reg, []);
        book.get(reg).push(f);
    }
    for (const arr of book.values()) {
        arr.sort((a, b) => getEtd(a) - getEtd(b));
    }
    return book;
}

function countViolations(book) {
    let disconnections = 0;
    let shortTurnarounds = 0;
    for (const flights of book.values()) {
        for (let i = 0; i < flights.length - 1; i++) {
            const curr = flights[i], next = flights[i + 1];
            if (!TAIL_CONSTRAINT_RULES[0].check(curr, next)) disconnections++;
            if (!TAIL_CONSTRAINT_RULES[1].check(curr, next)) shortTurnarounds++;
        }
    }
    return { disconnections, shortTurnarounds };
}

function scoreOf(v) {
    return v.disconnections * TAIL_CONSTRAINT_RULES[0].weight
        + v.shortTurnarounds * TAIL_CONSTRAINT_RULES[1].weight;
}

// ─── GREEDY CHAIN-BUILDER ─────────────────────────────────────────────────────

/**
 * Assigns each flight to the best available tail of the same acType.
 * Priority: (1) tail at correct airport, (2) tail with sufficient turnaround,
 * (3) most recently used tail (minimize idle time).
 */
export function greedyTailSchedule(flights) {
    const byAcType = new Map();
    for (const f of flights) {
        if (!byAcType.has(f.acType)) byAcType.set(f.acType, []);
        byAcType.get(f.acType).push(f);
    }

    const assignments = new Map();

    for (const [, acFlights] of byAcType) {
        const tails = [...new Set(acFlights.map(f => f.flightReg))];
        // tailState: reg → { airport: string|null, freeAt: ms }
        const tailState = new Map(tails.map(reg => [reg, { airport: null, freeAt: -Infinity }]));

        for (const flight of [...acFlights].sort((a, b) => getEtd(a) - getEtd(b))) {
            const std = getEtd(flight);

            // Pass 1: connected + sufficient turnaround
            let pool = tails.filter(reg => {
                const s = tailState.get(reg);
                return (s.airport === null || s.airport === flight.dep)
                    && (std - s.freeAt) / 60000 >= MINIMUM_TAT_MINUTES;
            });

            // Pass 2: any tail with sufficient turnaround (sacrifice connectivity)
            if (!pool.length) {
                pool = tails.filter(reg => (std - tailState.get(reg).freeAt) / 60000 >= MINIMUM_TAT_MINUTES);
            }

            const chosen = pool.length
                ? pool.reduce((best, reg) => {
                    const b = tailState.get(best), r = tailState.get(reg);
                    const bOk = b.airport === flight.dep;
                    const rOk = r.airport === flight.dep;
                    if (bOk !== rOk) return rOk ? reg : best;
                    // Among equal priority: pick the most recently used
                    return r.freeAt > b.freeAt ? reg : best;
                })
                // Last resort: tail that frees up soonest
                : tails.reduce((best, reg) =>
                    tailState.get(reg).freeAt < tailState.get(best).freeAt ? reg : best);

            assignments.set(flight._id, chosen);
            const eta = getEta(flight);
            tailState.set(chosen, {
                airport: flight.arr,
                freeAt: isNaN(eta) ? std + (flight.blockHours || 1) * 3600000 : eta,
            });
        }
    }

    return assignments;
}

// ─── LOCAL SEARCH (targeted swap repair) ──────────────────────────────────────

/**
 * Starts from the greedy solution then iteratively repairs disconnections by
 * swapping the "problem" flight with a flight on another tail that departs from
 * the correct airport. Accepts a swap only when it strictly reduces the violation
 * score.
 */
export function localSearchTailSchedule(flights, { maxIter = 100 } = {}) {
    let assignments = greedyTailSchedule(flights);
    let book = buildTailBook(flights, assignments);
    let violations = countViolations(book);
    let best = scoreOf(violations);

    for (let iter = 0; iter < maxIter && best > 0; iter++) {
        let improved = false;

        outer:
        for (const [reg, tailFlights] of book) {
            for (let i = 0; i < tailFlights.length - 1; i++) {
                const curr = tailFlights[i], next = tailFlights[i + 1];
                if (TAIL_CONSTRAINT_RULES[0].check(curr, next)) continue; // no disconnection here

                // next is at wrong airport; find a candidate on another tail departing from curr.arr
                for (const [otherReg, otherFlights] of book) {
                    if (otherReg === reg) continue;

                    for (const candidate of otherFlights) {
                        if (candidate.acType !== next.acType) continue;
                        if (candidate.dep !== curr.arr) continue;

                        const newAssignments = new Map(assignments);
                        newAssignments.set(next._id, otherReg);
                        newAssignments.set(candidate._id, reg);

                        const newBook = buildTailBook(flights, newAssignments);
                        const newV = countViolations(newBook);
                        const newScore = scoreOf(newV);

                        if (newScore < best) {
                            assignments = newAssignments;
                            book = newBook;
                            violations = newV;
                            best = newScore;
                            improved = true;
                            break outer;
                        }
                    }
                }
            }
        }

        if (!improved) break;
    }

    return assignments;
}

// ─── MAIN ENTRY ───────────────────────────────────────────────────────────────

/**
 * @param {Array}  flights   - flights from Redux state
 * @param {object} options
 *   algorithm: 'greedy' | 'local-search' (default)
 *   maxIter:   local search iterations (default 100)
 *
 * @returns {{
 *   assignments: Map<flightId, flightReg>,  // full map for all optimized flights
 *   changes:     Array<{ id, flightDesc, dep, arr, from, to }>,
 *   stats:       { before, after, changed, engine }
 * }}
 */
export function optimizeTailAssignment(flights, { algorithm = 'local-search', maxIter = 100 } = {}) {
    const valid = flights.filter(f => f.flightReg && f.acType && f.dep && f.arr);
    if (!valid.length) return { assignments: new Map(), changes: [], stats: null };

    const origBook = buildTailBook(valid, new Map(valid.map(f => [f._id, f.flightReg])));
    const before = countViolations(origBook);

    const assignments = algorithm === 'greedy'
        ? greedyTailSchedule(valid)
        : localSearchTailSchedule(valid, { maxIter });

    const optBook = buildTailBook(valid, assignments);
    const after = countViolations(optBook);

    const changes = [];
    for (const f of valid) {
        const to = assignments.get(f._id);
        if (to && to !== f.flightReg) {
            changes.push({
                id: f._id,
                flightDesc: f.flightDesc || `VN${f.flightNo}`,
                dep: f.dep,
                arr: f.arr,
                from: f.flightReg,
                to,
                flightDate: f.flightDate,
                etd: f.etd || f.std,
                eta: f.eta || f.sta,
            });
        }
    }

    return { assignments, changes, stats: { before, after, changed: changes.length, engine: algorithm } };
}
